import { useState, useEffect } from 'react';
import api from '../api';

const rs = {
  row:             { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #2a3040' },
  rowLabel:        { fontSize: '12px', color: '#6b7280' },
  rowValue:        { fontSize: '12px', fontWeight: '600', fontFamily: 'monospace' },
  badge:           { fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '4px', marginRight: '6px', color: '#fff', display: 'inline-block' },
  card:            { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '16px' },
  editBtn:         { background: 'none', color: '#6b7280', border: 'none', borderRadius: '6px', padding: '6px', fontSize: '14px', cursor: 'pointer', lineHeight: 1 },
  deleteBtn:       { background: 'none', color: '#fca5a5', border: 'none', borderRadius: '6px', padding: '6px', fontSize: '14px', cursor: 'pointer', lineHeight: 1 },
  overlay:         { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:           { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', width: '90%', maxWidth: '560px', maxHeight: '85vh', overflowY: 'auto' },
  modalHeader:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 16px', borderBottom: '1px solid #2a3040', position: 'sticky', top: 0, background: '#1e2329', zIndex: 1 },
  modalTitle:      { fontSize: '16px', fontWeight: '700', color: '#f0f2f5' },
  modalBody:       { padding: '20px' },
  modalFooter:     { display: 'flex', justifyContent: 'space-between', padding: '16px 20px', borderTop: '1px solid #2a3040', position: 'sticky', bottom: 0, background: '#1e2329' },
  closeBtn:        { background: 'none', border: 'none', color: '#6b7280', fontSize: '16px', cursor: 'pointer' },
  formField:       { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' },
  formLabel:       { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' },
  formInput:       { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '8px 10px', fontSize: '13px', fontFamily: 'system-ui, sans-serif' },
  formError:       { background: '#7f1d1d', color: '#fca5a5', border: '1px solid #991b1b', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '16px' },
  saveBtn:         { background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  backBtn:         { background: '#1e2329', color: '#9ca3af', border: '1px solid #2a3040', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', cursor: 'pointer' },
  addComponentBtn: { background: '#1e2329', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer', fontFamily: 'system-ui, sans-serif' },
  baseBadge:       { fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', background: '#0f766e', color: '#fff', flexShrink: 0 },
};

// ── RecipeCard ────────────────────────────────────────────────
// viewMode: 'pct' | 'shot' | 'kg' | 'bag'
// shotWeight: grams, used when viewMode === 'shot'
// showMeta: shows mould-specific/global + status footer
export function RecipeCard({ recipe, colours = [], viewMode = 'pct', shotWeight, isDirector, onEdit, onDelete, onShowParts, onHistory, showMeta = false }) {
  const baseComps = (recipe.recipe_components || []).filter(c => c.type === 'base');
  const otherComps = (recipe.recipe_components || []).filter(c => c.type !== 'base');
  const mbDots = (recipe.recipe_components || [])
    .filter(c => c.type === 'masterbatch' && c.material?.colour)
    .map(c => (colours || []).find(cl => cl.name === c.material.colour)?.hex)
    .filter(Boolean);

  const baseQty = viewMode === 'bag' ? 25000 : viewMode === 'kg' ? 1000 : viewMode === 'shot' ? (shotWeight || 100) : null;

  function fmtBase(pct) {
    if (!baseQty) return pct != null ? `${Number(pct).toLocaleString()}%` : '100%';
    if (pct == null) return `${Number(baseQty).toLocaleString()}g`;
    return `${parseFloat(((pct / 100) * baseQty).toFixed(1)).toLocaleString()}g`;
  }

  function fmtComp(c) {
    if (!baseQty) return `${Number(c.percentage).toLocaleString()}%`;
    const amt = parseFloat(((c.percentage / 100) * baseQty).toFixed(2));
    return <>{amt.toLocaleString()}g <span style={{ color: '#6b7280' }}>({Number(c.percentage).toLocaleString()}%)</span></>;
  }

  return (
    <div style={rs.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            {mbDots.map((hex, i) => (
              <span key={i} style={{ width: '13px', height: '13px', borderRadius: '50%', background: hex, border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0, display: 'inline-block' }} />
            ))}
            <span style={{ fontSize: '14px', fontWeight: '700' }}>{recipe.name}</span>
          </div>
          {recipe.description && <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{recipe.description}</div>}
        </div>
        <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '8px' }}>
          {onHistory && (
            <button style={{ ...rs.editBtn, color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }} onClick={() => onHistory(recipe)} title="Change history">
              <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: '12px' }} />
              <span>History</span>
            </button>
          )}
          {isDirector && (
            <>
              <button style={rs.editBtn} onClick={() => onEdit?.(recipe)} title="Edit"><i className="fa-regular fa-pen-to-square" /></button>
              <button style={rs.deleteBtn} onClick={() => onDelete?.(recipe.id)} title="Delete"><i className="fa-regular fa-trash-can" /></button>
            </>
          )}
        </div>
      </div>

      {baseComps.length > 0 ? baseComps.map(bc => (
        <div key={bc.id} style={rs.row}>
          <span style={rs.rowLabel}>
            <span style={{ ...rs.badge, background: '#0f766e' }}>BASE</span>
            {bc.material?.name}
          </span>
          <span style={{ ...rs.rowValue, color: '#14b8a6' }}>{fmtBase(bc.percentage)}</span>
        </div>
      )) : (
        <div style={rs.row}>
          <span style={rs.rowLabel}>
            <span style={{ ...rs.badge, background: '#0f766e' }}>BASE</span>
            {recipe.material?.name}
          </span>
          <span style={{ ...rs.rowValue, color: '#14b8a6' }}>{fmtBase(null)}</span>
        </div>
      )}

      {otherComps.map((c, i) => (
        <div key={c.id || i} style={rs.row}>
          <span style={rs.rowLabel}>
            <span style={{ ...rs.badge, background: c.type === 'masterbatch' ? '#1d4ed8' : c.type === 'base' ? '#0f766e' : '#166534' }}>
              {c.type === 'masterbatch' ? 'MB' : c.type === 'base' ? 'BASE' : 'ADD'}
            </span>
            {c.material?.name}
          </span>
          <span style={rs.rowValue}>{fmtComp(c)}</span>
        </div>
      ))}

      {otherComps.length === 0 && baseComps.length === 0 && (
        <div style={{ fontSize: '11px', color: '#6b7280', paddingTop: '8px' }}>Base material only</div>
      )}

      {(showMeta || onShowParts) && (
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #2a3040', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {onShowParts ? (
            <button onClick={() => onShowParts(recipe)}
              style={{ background: 'none', border: 'none', color: '#14b8a6', fontSize: '11px', fontWeight: '600', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <i className="fa-solid fa-cube" style={{ fontSize: '10px' }} />
              View linked parts
            </button>
          ) : (
            <span style={{ fontSize: '10px', color: '#6b7280' }}>
              {recipe.material?.family && (
                <span style={{ background: '#2a3040', color: '#9ca3af', padding: '2px 6px', borderRadius: '4px', marginRight: '6px' }}>{recipe.material.family}</span>
              )}
            </span>
          )}
          {showMeta && (
            <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: recipe.status === 'active' ? '#166534' : '#7f1d1d', color: recipe.status === 'active' ? '#86efac' : '#fca5a5' }}>
              {recipe.status || 'active'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── RecipeCreatorModal ────────────────────────────────────────
// Supports both create (recipe=null) and edit (recipe=object).
// mouldId can be null for global recipes.
export function RecipeCreatorModal({ mouldId, recipe, onSaved, onClose }) {
  const isNew = !recipe;
  const [materials, setMaterials] = useState([]);
  const [form, setForm] = useState({
    name:        recipe?.name        || '',
    description: recipe?.description || '',
  });

  const existingBases = recipe?.recipe_components?.filter(c => c.type === 'base') || [];
  const [bases, setBases] = useState(
    existingBases.length > 0
      ? existingBases.map(b => ({ material_id: String(b.material_id ?? b.material?.id ?? ''), percentage: b.percentage != null ? String(b.percentage) : '' }))
      : recipe?.material_id
      ? [{ material_id: String(recipe.material_id), percentage: '' }]
      : [{ material_id: '', percentage: '' }]
  );

  const existingComponents = recipe?.recipe_components?.filter(c => c.type !== 'base') || [];
  const [components, setComponents] = useState(
    existingComponents.map(c => ({ material_id: String(c.material_id ?? c.material?.id ?? ''), type: c.type, percentage: String(c.percentage), notes: c.notes || '' }))
  );

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => { api.get('/api/v1/materials').then(r => setMaterials(r.data)); }, []);

  const baseMaterialList = materials.filter(m => (m.type || 'material') === 'material');
  const masterbatches    = materials.filter(m => m.type === 'masterbatch');
  const additives        = materials.filter(m => m.type === 'additive');

  function updateBase(i, key, val)   { setBases(b => b.map((e, idx) => idx !== i ? e : { ...e, [key]: val })); }
  function removeBase(i)             { setBases(b => b.filter((_, idx) => idx !== i)); }
  function addComponent(type)        { setComponents(c => [...c, { material_id: '', type, percentage: '', notes: '' }]); }
  function updateComponent(i, k, v)  { setComponents(c => c.map((comp, idx) => idx !== i ? comp : { ...comp, [k]: v })); }
  function removeComponent(i)        { setComponents(c => c.filter((_, idx) => idx !== i)); }

  function save() {
    if (!form.name) { setError('Recipe name is required'); return; }
    const validBases = bases.filter(b => b.material_id);
    if (validBases.length === 0) { setError('At least one base material is required'); return; }
    setSaving(true);
    const payload = {
      material_id: parseInt(validBases[0].material_id),
      name:        form.name,
      description: form.description,
      components: [
        ...validBases.map(b => ({
          material_id: parseInt(b.material_id),
          type:        'base',
          percentage:  b.percentage !== '' ? parseFloat(b.percentage) : null,
        })),
        ...components
          .filter(c => c.material_id && c.percentage)
          .map(c => ({ material_id: parseInt(c.material_id), type: c.type, percentage: parseFloat(c.percentage), notes: c.notes || null })),
      ],
    };
    const req = isNew
      ? api.post('/api/v1/recipes', payload)
      : api.put(`/api/v1/recipes/${recipe.id}`, payload);
    req.then(r => onSaved(r.data)).catch(err => {
      setError(err.response?.data?.error || 'Save failed');
      setSaving(false);
    });
  }

  return (
    <div style={rs.overlay} onClick={onClose}>
      <div style={rs.modal} onClick={e => e.stopPropagation()}>
        <div style={rs.modalHeader}>
          <div style={rs.modalTitle}>{isNew ? 'New Recipe' : `Edit — ${recipe.name}`}</div>
          <button style={rs.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={rs.modalBody}>
          {error && <div style={rs.formError}>{error}</div>}
          <div style={rs.formField}>
            <label style={rs.formLabel}>Recipe name *</label>
            <input style={rs.formInput} value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Black PP 2% MB" />
          </div>
          <div style={rs.formField}>
            <label style={rs.formLabel}>Description</label>
            <input style={rs.formInput} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={rs.formLabel}>Base materials *</div>
            <div style={{ marginTop: '8px' }}>
              {bases.map((bm, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={rs.baseBadge}>BASE</span>
                  <select style={{ ...rs.formInput, flex: 3, minWidth: '140px' }}
                    value={bm.material_id} onChange={e => updateBase(i, 'material_id', e.target.value)}>
                    <option value="">— Select —</option>
                    {baseMaterialList.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <input type="number" placeholder="%" style={{ ...rs.formInput, width: '70px', flex: 'none' }}
                    value={bm.percentage} onChange={e => updateBase(i, 'percentage', e.target.value)} />
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>%</span>
                  {bases.length > 1 && (
                    <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}
                      onClick={() => removeBase(i)}>×</button>
                  )}
                </div>
              ))}
              <button style={rs.addComponentBtn}
                onClick={() => setBases(b => [...b, { material_id: '', percentage: '' }])}>
                + Add base material
              </button>
            </div>
          </div>

          <div>
            <div style={rs.formLabel}>Masterbatches & Additives</div>
            <div style={{ marginTop: '8px' }}>
              {components.map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ ...rs.baseBadge, background: c.type === 'masterbatch' ? '#1d4ed8' : '#166534' }}>
                    {c.type === 'masterbatch' ? 'MB' : 'ADD'}
                  </span>
                  <select style={{ ...rs.formInput, flex: 2, minWidth: '140px' }}
                    value={c.material_id} onChange={e => updateComponent(i, 'material_id', e.target.value)}>
                    <option value="">— Select —</option>
                    {(c.type === 'masterbatch' ? masterbatches : additives).map(m =>
                      <option key={m.id} value={m.id}>{m.name}</option>
                    )}
                  </select>
                  <input type="number" placeholder="%" style={{ ...rs.formInput, width: '70px', flex: 'none' }}
                    value={c.percentage} onChange={e => updateComponent(i, 'percentage', e.target.value)} />
                  <span style={{ fontSize: '11px', color: '#6b7280' }}>%</span>
                  <button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}
                    onClick={() => removeComponent(i)}>×</button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button style={rs.addComponentBtn} onClick={() => addComponent('masterbatch')}>+ Masterbatch</button>
                <button style={rs.addComponentBtn} onClick={() => addComponent('additive')}>+ Additive</button>
              </div>
            </div>
          </div>
        </div>
        <div style={rs.modalFooter}>
          <button style={rs.backBtn} onClick={onClose}>Cancel</button>
          <button style={{ ...rs.saveBtn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
            {saving ? 'Saving...' : isNew ? 'Save Recipe' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
